import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

interface ArchivarTramiteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Cantidad de escrituras a archivar; > 1 lo convierte en el diálogo del lote. */
  count?: number;
  /** Cuántas de las seleccionadas están bloqueadas (sólo se avisa, no impide archivar). */
  bloqueadasCount?: number;
  isSubmitting?: boolean;
  onConfirm: (motivo?: string) => void | Promise<void>;
}

export function ArchivarTramiteDialog({
  isOpen,
  onClose,
  count = 1,
  bloqueadasCount = 0,
  isSubmitting = false,
  onConfirm,
}: ArchivarTramiteDialogProps) {
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    if (isOpen) {
      setMotivo('');
    }
  }, [isOpen]);

  const esLote = count > 1;

  const handleSubmit = async () => {
    // El motivo es opcional: archivar es mantenimiento de rutina y se hace de a
    // decenas. Un campo obligatorio acá sólo produciría motivos de relleno.
    await onConfirm(motivo.trim() || undefined);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] min-w-0 max-w-md">
        <DialogHeader>
          <DialogTitle>
            {esLote ? `Archivar ${count} escrituras` : 'Archivar escritura'}
          </DialogTitle>
          <DialogDescription>
            {esLote
              ? 'Las escrituras dejan de aparecer en el listado. Podés volver a verlas con el filtro "Solo archivadas" y desarchivarlas cuando quieras.'
              : 'La escritura deja de aparecer en el listado. Podés volver a verla con el filtro "Solo archivadas" y desarchivarla cuando quieras.'}
          </DialogDescription>
        </DialogHeader>

        {bloqueadasCount > 0 && (
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {bloqueadasCount === 1
              ? '1 de las escrituras seleccionadas está bloqueada. Se archivará igualmente.'
              : `${bloqueadasCount} de las escrituras seleccionadas están bloqueadas. Se archivarán igualmente.`}
          </p>
        )}

        <Textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Motivo del archivado (opcional)"
          maxLength={1000}
          className="max-h-40 overflow-y-auto"
          disabled={isSubmitting}
          autoFocus
        />

        <DialogFooter className="gap-2 sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={isSubmitting}>
            {esLote ? `Archivar ${count}` : 'Archivar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ArchivarTramiteDialog;
